/**
 * Two languages, one set of components.
 *
 * The site had an "English" link in the nav that led to a single 100-line static page with a
 * hardcoded city list and no map. It looked like a language toggle and behaved like a dead end.
 * There was no i18n system at all.
 *
 * This is the whole system. No library: the site has ~30 strings and two locales, and next-intl
 * would add a dependency, a middleware and a build step to solve a problem that a plain object
 * solves. Every page component takes a `locale` and calls `t(locale)`.
 *
 * URL shape is deliberate — Spanish keeps its existing URLs (they rank, and moving them would
 * throw away the SEO this site is built on):
 *
 *     ES   /                         /parques-caninos      /parques-caninos/madrid
 *     EN   /en                       /en/dog-parks         /en/dog-parks/madrid
 *
 * The city SLUG stays the same in both. It is a Spanish place name; "madrid" is not translatable,
 * and keeping it identical means one canonical entity per city with two language views.
 */

export const LOCALES = ['es', 'en']
export const DEFAULT_LOCALE = 'es'

/** The path prefix for a locale. Spanish is at the root, so it has none. */
export function localePrefix(locale) {
  return locale === 'en' ? '/en' : ''
}

/** The cities-index path for a locale. */
export function citiesPath(locale) {
  return locale === 'en' ? '/en/dog-parks' : '/parques-caninos'
}

/** A city page path. The slug is identical in both languages. */
export function cityPath(locale, slug) {
  return `${citiesPath(locale)}/${slug}`
}

/** A park page path. */
export function parkPath(locale, citySlug, parkSlug) {
  return `${citiesPath(locale)}/${citySlug}/${parkSlug}`
}

/**
 * Province / region slug. MUST agree with slugify() in scripts/spain/generate_admin_facts.mjs —
 * if these two drift, every city page links to a 404 hub. Bilingual official names
 * ("València / Valencia") take their FIRST form, not a concatenation of both.
 */
export function adminSlug(value = '') {
  return (
    String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .split('/')[0]
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'x'
  )
}

/** A province / region hub path. */
export function adminPath(locale, kind, slug) {
  if (locale === 'en') {
    return kind === 'province' ? `/en/dog-parks/province/${slug}` : `/en/dog-parks/region/${slug}`
  }
  return kind === 'province'
    ? `/parques-caninos/provincia/${slug}`
    : `/parques-caninos/comunidad/${slug}`
}

/** The same page in the other language — for the nav toggle and for hreflang. */
export function alternatePath(locale, kind, citySlug, parkSlug) {
  const other = locale === 'en' ? 'es' : 'en'
  if (kind === 'home') return other === 'en' ? '/en' : '/'
  if (kind === 'cities') return citiesPath(other)
  if (kind === 'city') return cityPath(other, citySlug)
  if (kind === 'park') return parkPath(other, citySlug, parkSlug)
  return other === 'en' ? '/en' : '/'
}

const DICT = {
  es: {
    // chrome
    cities: 'Ciudades',
    contact: 'Contacto',
    otherLang: 'English',
    home: 'Inicio',
    breadcrumb: 'Migas de pan',

    // home
    homeEyebrow: 'Directorio nacional · datos de OpenStreetMap',
    homeTitle: 'Parques caninos cerca de ti',
    homeLede:
      'Todas las zonas caninas registradas en España, situadas sobre el mapa. Busca por municipio, o deja que el navegador te ordene los parques por cercanía.',
    parks: 'Parques',
    municipalities: 'Municipios',
    nearMe: 'Ver parques cerca de mí',
    allCities: 'Ver todas las ciudades',
    topCities: 'Las ciudades con más parques',
    ofN: (a, b) => `${a} de ${b}`,
    seeAllMunicipalities: (n) => `Ver los ${n} municipios →`,
    mapNote: 'el tamaño del punto es el número de parques · toca uno para abrir su mapa',

    // cities index
    citiesEyebrow: 'Índice nacional · datos de OpenStreetMap',
    citiesTitle: 'Parques caninos por ciudad',
    citiesLede:
      'Todos los municipios de España con al menos una zona canina registrada. Ordenados por número de parques, porque el reparto no es uniforme: Madrid tiene 153 y la mayoría de los municipios tienen uno.',
    searchCity: 'Busca un municipio, provincia o comunidad…',
    mostParks: 'Más parques',
    az: 'A-Z',
    nMunicipalities: (n) => `${n} municipios`,
    noMatch: (q) => `No hay ningún municipio que coincida con “${q}”. Prueba con el nombre de la provincia.`,
    notListed:
      '¿Tu municipio no aparece? Sólo listamos zonas caninas registradas en OpenStreetMap. Si conoces una que falta,',
    writeUs: 'escríbenos',
    andWeAdd: 'y la añadimos.',

    // city page
    cityTitle: (c) => `Parques caninos en ${c}`,
    cityLede: (c) =>
      `Todos los espacios caninos registrados en ${c}, situados sobre el mapa. Sin adivinar: cada punto es una ubicación real.`,
    fenced: 'Vallados',
    named: 'Con nombre',
    parksLabel: (n) => (n === 1 ? 'Parque' : 'Parques'),
    theNParks: (n) => (n === 1 ? 'El parque' : `Los ${n} parques`),
    orderedFromCentre: 'ordenados desde el centro',
    searchIn: (c) => `Buscar en ${c}: nombre o zona`,
    nearby: 'Cerca de mí',
    searching: 'Buscando…',
    sortedByDistance: 'Ordenado por cercanía',
    scrollList: 'desplaza la lista',
    nParks: (n) => `${n} ${n === 1 ? 'parque' : 'parques'}`,
    nOfMParks: (n, m) => `${n} de ${m} ${m === 1 ? 'parque' : 'parques'}`,

    // map chrome — these render inside a client component, so they never reached the SSR HTML
    // and the Spanish strings were shipping untranslated on every English page.
    mapAria: (c) => `Mapa de parques caninos en ${c}`,
    mapCount: (n) => `${n} ${n === 1 ? 'parque' : 'parques'} · toca un punto para abrirlo`,
    mapAttribution: 'Mapa',
    mapData: 'datos',
    mapFallback: (n) =>
      n === 1
        ? 'Tu navegador no puede mostrar el mapa interactivo. El parque sigue listado abajo, con su ubicación exacta.'
        : `Tu navegador no puede mostrar el mapa interactivo. Los ${n} parques siguen listados abajo, con su ubicación exacta.`,

    // cookie bar — renders on EVERY page in both trees
    cookieAria: 'Aviso de cookies',
    cookieNotice: 'Usamos cookies propias y de terceros (analítica) para mejorar el sitio.',
    cookieMore: 'Más información',
    cookieAccept: 'Aceptar',

    // map popup
    openCard: 'Abrir ficha',

    // geolocation + degraded states. These only render when something FAILS, so they never showed
    // up in a happy-path render — and shipped in Spanish to English readers for exactly that reason.
    geoUnsupported: 'Tu navegador no soporta geolocalización.',
    sortedFromYou: 'ordenados desde tu ubicación',
    geoDenied: 'no pudimos obtener tu ubicación',
    geoUnavailable: 'tu navegador no permite geolocalización',
    mapFallbackCities: (n) =>
      `Tu navegador no puede mostrar el mapa interactivo. Las ${n} ciudades siguen listadas abajo.`,

    // the honest-data note on a city page
    aboutDataLead: 'Sobre los datos:',
    aboutData:
      'los parques proceden de OpenStreetMap, que mantienen voluntarios. La mayoría de las áreas caninas de España no tienen nombre registrado, y de muchas no consta si están valladas o iluminadas. Cuando un dato no consta, aquí no aparece: preferimos no mostrarlo a inventarlo.',
    tellUs: 'Avísanos si falta algo',
    correctionSubject: 'Corrección en',

    // the business promo on a city page
    promoTitle: (c) => `¿Tienes un negocio para perros en ${c}?`,
    promoSub: (c) =>
      `Veterinarias, peluquerías, adiestradores y guarderías: llega a los dueños de perros de ${c} que ya están buscando aquí.`,
    promoCta: 'Destaca tu negocio',

    // park page
    dogArea: 'Zona canina',
    directions: 'Cómo llegar',
    allParksIn: (c) => `← Todos los parques de ${c}`,
    latitude: 'Latitud',
    longitude: 'Longitud',
    dataPoint: 'Dato registrado',
    dataPoints: 'Datos registrados',
    details: 'Detalles',
    osmRecords: 'Lo que registra OpenStreetMap',
    otherNearby: 'Otros parques caninos cerca',
    fromThisPark: 'de este parque',
    dataCaveat:
      'Los datos vienen de OpenStreetMap y los mantiene la comunidad, así que pueden estar incompletos. Si algo aquí no es correcto,',
    orEditOn: 'o edítalo directamente en',
    noCoords:
      'Este parque está registrado en OpenStreetMap pero todavía no tiene coordenadas, así que no podemos situarlo en el mapa.',
  },

  en: {
    cities: 'Cities',
    contact: 'Contact',
    otherLang: 'Español',
    home: 'Home',
    breadcrumb: 'Breadcrumb',

    homeEyebrow: 'National directory · OpenStreetMap data',
    homeTitle: 'Dog parks near you, in Spain',
    homeLede:
      'Every registered dog area in Spain, placed on the map. Search by town, or let your browser order them by how close they are.',
    parks: 'Parks',
    municipalities: 'Towns',
    nearMe: 'Find dog parks near me',
    allCities: 'See all towns',
    topCities: 'The towns with the most dog parks',
    ofN: (a, b) => `${a} of ${b}`,
    seeAllMunicipalities: (n) => `See all ${n} towns →`,
    mapNote: 'dot size is the number of parks · tap one to open its map',

    citiesEyebrow: 'National index · OpenStreetMap data',
    citiesTitle: 'Dog parks by town',
    citiesLede:
      'Every town in Spain with at least one registered dog area. Ordered by number of parks, because the spread is not even: Madrid has 153 and most towns have one.',
    searchCity: 'Search a town, province or region…',
    mostParks: 'Most parks',
    az: 'A-Z',
    nMunicipalities: (n) => `${n} towns`,
    noMatch: (q) => `No town matches “${q}”. Try the name of the province.`,
    notListed:
      'Town not listed? We only list dog areas registered in OpenStreetMap. If you know one that is missing,',
    writeUs: 'write to us',
    andWeAdd: 'and we will add it.',

    cityTitle: (c) => `Dog parks in ${c}`,
    cityLede: (c) =>
      `Every registered dog area in ${c}, placed on the map. No guesswork: each dot is a real location.`,
    fenced: 'Fenced',
    named: 'Named',
    parksLabel: (n) => (n === 1 ? 'Park' : 'Parks'),
    theNParks: (n) => (n === 1 ? 'The park' : `The ${n} parks`),
    orderedFromCentre: 'ordered from the centre',
    searchIn: (c) => `Search in ${c}: name or area`,
    nearby: 'Near me',
    searching: 'Locating…',
    sortedByDistance: 'Sorted by distance',
    scrollList: 'scroll the list',
    nParks: (n) => `${n} ${n === 1 ? 'park' : 'parks'}`,
    nOfMParks: (n, m) => `${n} of ${m} ${m === 1 ? 'park' : 'parks'}`,

    mapAria: (c) => `Map of dog parks in ${c}`,
    mapCount: (n) => `${n} ${n === 1 ? 'park' : 'parks'} · tap a point to open it`,
    mapAttribution: 'Map',
    mapData: 'data',
    mapFallback: (n) =>
      n === 1
        ? 'Your browser cannot show the interactive map. The park is still listed below, with its exact location.'
        : `Your browser cannot show the interactive map. All ${n} parks are still listed below, with their exact locations.`,

    cookieAria: 'Cookie notice',
    cookieNotice: 'We use our own and third-party cookies (analytics) to improve the site.',
    cookieMore: 'More information',
    cookieAccept: 'Accept',

    openCard: 'Open details',

    geoUnsupported: 'Your browser does not support geolocation.',
    sortedFromYou: 'ordered from your location',
    geoDenied: 'we could not get your location',
    geoUnavailable: 'your browser does not allow geolocation',
    mapFallbackCities: (n) =>
      `Your browser cannot show the interactive map. All ${n} towns are still listed below.`,

    aboutDataLead: 'About the data:',
    aboutData:
      'the parks come from OpenStreetMap, maintained by volunteers. Most dog areas in Spain have no registered name, and for many it is not recorded whether they are fenced or lit. Where a fact is not recorded, it does not appear here: we would rather leave it out than invent it.',
    tellUs: 'Tell us if something is missing',
    correctionSubject: 'Correction to',

    promoTitle: (c) => `Run a dog business in ${c}?`,
    promoSub: (c) =>
      `Vets, groomers, trainers and boarding: reach the dog owners in ${c} who are already looking here.`,
    promoCta: 'Feature your business',

    dogArea: 'Dog area',
    directions: 'Directions',
    allParksIn: (c) => `← All dog parks in ${c}`,
    latitude: 'Latitude',
    longitude: 'Longitude',
    dataPoint: 'recorded detail',
    dataPoints: 'recorded details',
    details: 'Details',
    osmRecords: 'What OpenStreetMap records',
    otherNearby: 'Other dog parks nearby',
    fromThisPark: 'from this park',
    dataCaveat:
      'The data comes from OpenStreetMap and is maintained by the community, so it can be incomplete. If something here is wrong,',
    orEditOn: 'or edit it directly on',
    noCoords:
      'This park is registered in OpenStreetMap but has no coordinates yet, so we cannot place it on the map.',
  },
}

export function t(locale) {
  return DICT[locale] || DICT[DEFAULT_LOCALE]
}
